import { CheckIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  AspectRatio,
  Box,
  Button,
  Checkbox,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Spinner,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useGetVideoAvatarsQuery } from "store/aiHub/aiGenerationApi";
import CustomAvatarContent from "./CustomAvatarContent";
import { useSelector } from "react-redux";

const ALL_GENDERS = ["m", "f"];
// Ensure these match the actual filter values your API expects
const ALL_AGES = ["adult", "senior"]; // UI offers Adult & Senior
const ALL_LOCATIONS = ["indoor", "outdoor"];

function HoverPreviewCard({ avatar, isSelected, onSelect }) {
  const checkIconColor = useColorModeValue("brand.800", "purple.800");
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);


  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        videoRef.current.play().catch((err) => {
          console.warn("Video playback failed:", err);
        });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered]);

  return (
    <Box onClick={onSelect}>
      <AspectRatio ratio={1}>
        <Box
          overflow="hidden"
          borderRadius="8px"
          border="1px solid"
          borderColor={isSelected ? "brand.800" : "transparent"}
          _hover={{ shadow: "md", cursor: "pointer", borderColor: "brand.800" }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          position="relative"
        >
          {isSelected && (
            <Box position="absolute" top="8px" left="8px" zIndex={3}>
              <CheckIcon
                color="white"
                bg={checkIconColor}
                borderRadius="full"
                p="1"
                boxSize={5}
              />
            </Box>
          )}

          {/* Image */}
          <Image
            src={avatar.preview_image_1_1}
            alt={avatar.creator_name}
            objectFit="cover"
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            style={{
              opacity: isHovered ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
            }}
          />

          {/* Spinner */}
          {isHovered && isLoading && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              zIndex={2}
            >
              <Spinner size="md" color="white" />
            </Box>
          )}

          {/* Video */}
          <video
            ref={videoRef}
            src={avatar.preview_video_1_1}
            muted
            loop
            playsInline
            onCanPlay={() => setIsLoading(false)}
            onWaiting={() => setIsLoading(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        </Box>
      </AspectRatio>
      <Box mt={2}>
        <Text fontSize="xs" fontWeight="bold">
          {avatar.creator_name}
        </Text>
      </Box>
    </Box>
  );
}

function RealisticAvatarContent({ selectedAvatar, onSelectAvatar }) {
  const [page, setPage] = useState(1);
  const [avatars, setAvatars] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedAges, setSelectedAges] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const filtersOnly = useMemo(() => {
    const f = {};

    if (
      selectedGenders.length > 0 &&
      selectedGenders.length < ALL_GENDERS.length
    ) {
      f.gender = selectedGenders.join(",");
    }

    if (selectedAges.length > 0 && selectedAges.length < ALL_AGES.length) {
      f.age_range = selectedAges.join(",");
    }

    if (
      selectedLocations.length > 0 &&
      selectedLocations.length < ALL_LOCATIONS.length
    ) {
      f.location = selectedLocations.join(",");
    }

    return f;
  }, [selectedGenders, selectedAges, selectedLocations]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [filtersOnly]);

  const videoAvatarFilters = useMemo(() => {
    const params = { ...filtersOnly };
    // If filters are applied, always set page = 1
    params.page = Object.keys(filtersOnly).length > 0 ? 1 : page;
    params.page_size = 20;
    return params;
  }, [filtersOnly, page]);

  const shouldFetch = page > 0;

  const {
    data: videoAvatars,
    isLoading: isvideoAvatarsLoading,
    isFetching: isvideoAvatarsFetching,
  } = useGetVideoAvatarsQuery(videoAvatarFilters, { skip: !shouldFetch });

  useEffect(() => {
    if (videoAvatars?.results) {
      setAvatars((prevAvatars) => {
        const newAvatars = videoAvatars.results.filter(
          (a) => !prevAvatars.some((prev) => prev.id === a.id)
        );
        return page === 1
          ? videoAvatars.results
          : [...prevAvatars, ...newAvatars];
      });
      setHasMore(Boolean(videoAvatars.next));
    }
  }, [videoAvatars, page]);

  const loadMoreAvatars = () => {
    if (hasMore && !isvideoAvatarsFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const handleGenderChange = (gender) => {
    setSelectedGenders((prev) =>
      prev.includes(gender)
        ? prev.filter((g) => g !== gender)
        : [...prev, gender]
    );
  };

  const handleAgeChange = (age) => {
    setSelectedAges((prev) =>
      prev.includes(age) ? prev.filter((a) => a !== age) : [...prev, age]
    );
  };

  const handleLocationChange = (location) => {
    setSelectedLocations((prev) =>
      prev.includes(location)
        ? prev.filter((loc) => loc !== location)
        : [...prev, location]
    );
  };

  const isFiltering =
    selectedGenders.length > 0 ||
    selectedAges.length > 0 ||
    selectedLocations.length > 0;

  return (
    <Box width={"full"}>
      {/* Filters */}
      <Flex direction="column" gap={2} flexShrink={0}>
        <Flex mb={2} gap={2}>
          {/* Gender Menu */}
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              colorScheme="brand"
              _expanded={{ bg: "orange.600" }}
              rightIcon={<ChevronDownIcon />}
              size="xs"
            >
              Gender
            </MenuButton>
            <MenuList p={2} borderRadius="8px" mt={-1}>
              <MenuItem
                closeOnSelect={false}
                borderRadius="5px"
                _hover={{ backgroundColor: "gray.50" }}
              >
                <Checkbox
                  size="sm"
                  colorScheme="brand"
                  isChecked={selectedGenders.includes("m")}
                  onChange={() => handleGenderChange("m")}
                >
                  <Text fontSize="12px">Male</Text>
                </Checkbox>
              </MenuItem>
              <MenuItem
                closeOnSelect={false}
                borderRadius="5px"
                _hover={{ backgroundColor: "gray.50" }}
              >
                <Checkbox
                  size="sm"
                  colorScheme="brand"
                  isChecked={selectedGenders.includes("f")}
                  onChange={() => handleGenderChange("f")}
                >
                  <Text fontSize="12px">Female</Text>
                </Checkbox>
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Age Menu */}
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              colorScheme="brand"
              _expanded={{ bg: "orange.600" }}
              rightIcon={<ChevronDownIcon />}
              size="xs"
            >
              Age
            </MenuButton>
            <MenuList p={2} borderRadius="8px" mt={-1}>
              <MenuItem
                closeOnSelect={false}
                borderRadius="5px"
                _hover={{ backgroundColor: "gray.50" }}
              >
                <Checkbox
                  size="sm"
                  colorScheme="brand"
                  isChecked={selectedAges.includes("adult")}
                  onChange={() => handleAgeChange("adult")}
                >
                  <Text fontSize="12px">Adult</Text>
                </Checkbox>
              </MenuItem>
              <MenuItem
                closeOnSelect={false}
                borderRadius="5px"
                _hover={{ backgroundColor: "gray.50" }}
              >
                <Checkbox
                  size="sm"
                  colorScheme="brand"
                  isChecked={selectedAges.includes("senior")}
                  onChange={() => handleAgeChange("senior")}
                >
                  <Text fontSize="12px">Senior</Text>
                </Checkbox>
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Location Menu */}
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              colorScheme="brand"
              _expanded={{ bg: "orange.600" }}
              rightIcon={<ChevronDownIcon />}
              size="xs"
            >
              Location
            </MenuButton>
            <MenuList p={2} borderRadius="8px" mt={-1}>
              <MenuItem
                closeOnSelect={false}
                borderRadius="5px"
                _hover={{ backgroundColor: "gray.50" }}
              >
                <Checkbox
                  size="sm"
                  colorScheme="brand"
                  isChecked={selectedLocations.includes("indoor")}
                  onChange={() => handleLocationChange("indoor")}
                >
                  <Text fontSize="12px">Indoor</Text>
                </Checkbox>
              </MenuItem>
              <MenuItem
                closeOnSelect={false}
                borderRadius="5px"
                _hover={{ backgroundColor: "gray.50" }}
              >
                <Checkbox
                  size="sm"
                  colorScheme="brand"
                  isChecked={selectedLocations.includes("outdoor")}
                  onChange={() => handleLocationChange("outdoor")}
                >
                  <Text fontSize="12px">Outdoor</Text>
                </Checkbox>
              </MenuItem>
            </MenuList>
          </Menu>

          {(selectedGenders.length > 0 ||
            selectedAges.length > 0 ||
            selectedLocations.length > 0) && (
              <Button
                size="xs"
                colorScheme="brand"
                variant="outline"
                onClick={() => {
                  setSelectedGenders([]);
                  setSelectedAges([]);
                  setSelectedLocations([]);
                  setPage(1);
                  setHasMore(true);
                }}
                ml={2}
              >
                {`Clear all (${selectedGenders.length +
                  selectedAges.length +
                  selectedLocations.length
                  })`}
              </Button>
            )}
        </Flex>
      </Flex>

      {/* Scrollable Grid Container */}
      <Box id="scrollableDiv" flex="1" maxH="400px" overflowY="auto" p={2}>
        {(avatars.length === 0 && isvideoAvatarsLoading) ||
          (isFiltering && isvideoAvatarsFetching) ? (
          <Flex alignItems="center" justifyContent="center" h="50vh">
            <Spinner size="md" />
          </Flex>
        ) : (
          <InfiniteScroll
            dataLength={avatars?.length}
            next={loadMoreAvatars}
            hasMore={hasMore}
            loader={
              <Flex alignItems="center" justifyContent="center" py={4}>
                <Spinner size="md" />
              </Flex>
            }
            scrollableTarget="scrollableDiv"
          >
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              {avatars.map((avatar) => {
                const isSelected = selectedAvatar?.id === avatar.id;

                return (
                  <HoverPreviewCard
                    key={avatar.id}
                    avatar={avatar}
                    isSelected={isSelected}
                    onSelect={() => {
                      if (isSelected) {
                        onSelectAvatar(null); // Deselect if already selected
                      } else {
                        onSelectAvatar(avatar); // Select if not selected
                      }
                    }}
                  />
                );
              })}
            </SimpleGrid>
          </InfiniteScroll>
        )}
      </Box>
    </Box>
  );
}

export default function AvatarTabPanel({ selectedAvatar, onSelectAvatar }) {

  const userInfo = useSelector((state) => state.auth?.userInfo);

  return (
    <Box width="full">
      {/* Tabs header above filters */}
      <Tabs
        variant="unstyled" // use unstyled to avoid default full-width underline
        colorScheme="brand"
        size="sm"
      >
        <TabList gap={4}>
          <Tab
            _selected={{
              borderBottom: '2px solid',
              borderColor: 'brand.500', // or whatever color you want
              fontWeight: 'bold',
            }}
          >
            Realistic Avatar
          </Tab>
          <Tab
            _selected={{
              borderBottom: '2px solid',
              borderColor: 'brand.500',
              fontWeight: 'bold',
            }}
          >
            Custom Avatar
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <RealisticAvatarContent
              selectedAvatar={selectedAvatar}
              onSelectAvatar={onSelectAvatar}
            />
          </TabPanel>
          <TabPanel px={0}>

            {["abhinav@blinkdigital.in", "rikki@blinkdigital.in"].includes(userInfo?.email) ? (
              <CustomAvatarContent
                selectedAvatar={selectedAvatar}
                onSelectAvatar={onSelectAvatar}
              />
            ) : (
              <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                h="200px"
              >
                <Text mt={4} fontSize="sm" mb="20px">
                  Please contact hustle.app@blinkdigital.in to get access to this exclusive feature.
                </Text>
              </Flex>
            )}


          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

